import { observer } from "mobx-react-lite";
import { posthog } from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import * as React from "react";

import { useAuthStore } from "src/auth";
import { useAppMetadata } from "src/contexts/AppMetadataContext";
import { useCookieConsent } from "src/contexts/CookieConsentContext";

export const PosthogInit = observer(function PosthogInit(
  props: React.PropsWithChildren,
) {
  const authStore = useAuthStore();
  const appMetadata = useAppMetadata();
  const { consent } = useCookieConsent();

  const hasAnalyticsConsent = consent?.categories.analytics === true;

  React.useEffect(() => {
    if (hasAnalyticsConsent) {
      if (posthog.has_opted_out_capturing()) {
        console.info("[PostHog] Opting in (consent given)");
        posthog.opt_in_capturing();
      }
    } else if (consent !== null) {
      console.info("[PostHog] Opting out (consent declined)");
      posthog.opt_out_capturing();
    }
  }, [hasAnalyticsConsent, consent]);

  React.useEffect(() => {
    if (!authStore.isAuthenticated || !hasAnalyticsConsent) {
      return;
    }

    console.info("[PostHog] Identify");
    posthog.identify(
      authStore.user.email ?? authStore.user.id,
      {
        email: authStore.user.email,
        id: authStore.user.id,
        name: authStore.user.name,
        isAthlete: authStore.isAthlete,
        isCoach: authStore.isCoach,
        appVersion: appMetadata.version.current,
      },
      {
        invitationCode: authStore.authenticatedData.invitationCode ?? "none",
      },
    );
    posthog.capture("Authenticated", {
      distinctId: authStore.user.email ?? authStore.user.id,
      appVersion: appMetadata.version.current,
    });
  }, [authStore.isAuthenticated, hasAnalyticsConsent]);

  return <PostHogProvider client={posthog}>{props.children}</PostHogProvider>;
});
